import React from 'react';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormCheckbox } from '../../../components/Form/Checkbox/FormCheckbox';
import { FormForm } from '../../../components/Form/FormForm';
import { FormTextField } from '../../../components/Form/TextField/FormTextField';
import { Stack } from '../../../components/Stack/Stack';
import { graphql } from '../../../generated';
import { t } from '../../../locales/translate';
import { handleError } from '../../../model/notification';
import { createVenueLayout, VenueLayout } from '../../../model/venue';
import { usePermission } from '../../../model/permission';
import { FormNumberField } from '../../../components/Form/NumberField/FormNumberField';
import { Spacer } from '../../../components/Spacer/Spacer';
import { tapPresent } from '../../../util/values';
import { useMutationHandler } from '../../../model/graphql';

const UPDATE_VENUE_LAYOUT = graphql(`
  mutation UpdateVenueLayout($id: ID!, $name: String, $archived: Boolean) {
    updateVenueLayout(input: { id: $id, name: $name, archived: $archived }) {
      venueLayout {
        id
        name
        archived
        permissions {
          id
          destroy
          update
        }
      }
    }
  }
`);

interface Props {
  venueId?: string;
  venueLayout?: VenueLayout;
  onClose: () => void;
  onSaved: (venueLayout: VenueLayout) => void;
}

export function VenueLayoutForm({ venueId, venueLayout, onClose, onSaved }: Props): JSX.Element {
  const creating = !venueLayout?.id;
  const [updateVenueLayout] = useMutationHandler(UPDATE_VENUE_LAYOUT);
  const { model, fields, dirty, valid, onSubmit } = useForm<VenueLayout>({
    model: { scale: 0.5, ...venueLayout } as VenueLayout,
    validations: {
      name: 'required',
      // TODO: Why is this not typed?
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      scale: (f: any) => ((f.model.scale || 0) > 0 ? [] : ['Scale must be positive']),
    },
    onSubmit: async () => {
      if (creating) {
        if (!venueId) throw new Error('venueId is required for creation');
        const createdLayout = await createVenueLayout(venueId, { ...model });
        tapPresent(createdLayout, onSaved);
      } else {
        const response = await updateVenueLayout({
          variables: { id: model.id, name: model.name ?? null, archived: model.archived ?? null },
        });
        tapPresent(response.data?.updateVenueLayout?.venueLayout, onSaved);
      }
      onClose?.();
    },
    onSubmitError: handleError,
  });
  const canUpdateCapacity = usePermission(model, 'updateCapacity') || creating;

  return (
    <FormForm onSubmit={onSubmit} onClose={onClose} canSubmit={dirty && valid}>
      <Stack>
        <FormTextField {...fields.name} label={t.model.venueLayout.name} />
        <FormNumberField
          {...fields.capacity}
          label={t.model.venueLayout.capacity}
          disabled={!canUpdateCapacity}
          description={canUpdateCapacity ? '' : t.model.venueLayout.capacitySetBySeatMap}
        />
        {!creating && (
          <>
            <Spacer />
            <FormCheckbox {...fields.archived} label={t.general.archived} />
          </>
        )}
        {creating && (
          <>
            <FormTextField
              {...fields.seatsIoId}
              label={t.model.event.chartsKey}
              description={`${t.model.event.chartsKey} ${t.general.description}`}
            />
            <FormNumberField
              {...fields.scale}
              label={t.model.event.chartsScaleDescription}
              step={0.01}
              description={t.model.event.chartsScaleDescription}
              // TODO: min={0.01}
            />
          </>
        )}
      </Stack>
    </FormForm>
  );
}
